import { useEffect, useState } from 'react';

// Index right after the last of `pinned` present in `order` (0 when none are).
const afterLastPinned = <K extends string>(order: readonly K[], pinned: readonly K[]): number =>
  order.reduce((at, key, idx) => (pinned.includes(key) ? idx + 1 : at), 0);

/** Moves `key` to the front of `order`, right after the columns already in `pinned`. */
export function pinColumn<K extends string>(order: readonly K[], key: K, pinned: readonly K[]): K[] {
  if (!order.includes(key)) return [...order];
  const without = order.filter((k) => k !== key);
  const others = pinned.filter((k) => k !== key);
  without.splice(afterLastPinned(without, others), 0, key);
  return without;
}

/**
 * Returns an unpinned `key` to its default slot: right after its nearest default predecessor still
 * in `order`, but never ahead of the columns that stay pinned (`remainingPinned`).
 */
export function unpinColumn<K extends string>(
  order: readonly K[],
  key: K,
  remainingPinned: readonly K[],
  defaultOrder: readonly K[]
): K[] {
  if (!order.includes(key)) return [...order];
  const without = order.filter((k) => k !== key);
  const defaultIdx = defaultOrder.indexOf(key);
  let afterPredecessor = 0;
  for (let p = defaultIdx - 1; p >= 0; p--) {
    const at = without.indexOf(defaultOrder[p]);
    if (at >= 0 && !remainingPinned.includes(defaultOrder[p])) {
      afterPredecessor = at + 1;
      break;
    }
  }
  without.splice(Math.max(afterPredecessor, afterLastPinned(without, remainingPinned)), 0, key);
  return without;
}

const readPinned = <K extends string>(storageKey: string | null, known: readonly K[]): K[] => {
  if (!storageKey) return [];
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((k, idx): k is K => known.includes(k) && parsed.indexOf(k) === idx);
  } catch {
    return [];
  }
};

const writePinned = (storageKey: string | null, pinned: string[]): void => {
  if (!storageKey) return;
  try {
    if (pinned.length === 0) window.localStorage.removeItem(storageKey);
    else window.localStorage.setItem(storageKey, JSON.stringify(pinned));
  } catch {
    // localStorage can be unavailable (private mode, quota); pins still apply for this session.
  }
};

/**
 * Which columns the user pinned to the front, in pin order, persisted under `storageKey` (null
 * disables persistence). The caller moves the columns in its own column order with
 * pinColumn()/unpinColumn(); this only tracks the pinned set. `known` must be a stable array.
 */
export function usePinnedColumns<K extends string>(storageKey: string | null, known: readonly K[]) {
  const [pinnedColumns, setPinnedColumnsState] = useState<K[]>(() => readPinned(storageKey, known));

  useEffect(() => {
    setPinnedColumnsState(readPinned(storageKey, known));
  }, [storageKey, known]);

  const setPinnedColumns = (next: K[]) => {
    setPinnedColumnsState(next);
    writePinned(storageKey, next);
  };

  return {
    pinnedColumns,
    isPinned: (key: string) => (pinnedColumns as string[]).includes(key),
    setPinnedColumns,
    clearPinnedColumns: () => setPinnedColumns([]),
  };
}
